import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js/max";
import examples from "libphonenumber-js/mobile/examples";

const DEFAULT_COUNTRY = "IS";
const REGION_NAMES = typeof Intl !== "undefined" && Intl.DisplayNames
  ? new Intl.DisplayNames(["en"], { type: "region" })
  : null;

function normalizeCountry(country) {
  const next = String(country || "").trim().toUpperCase();
  return getCountries().includes(next) ? next : DEFAULT_COUNTRY;
}

function sanitizePhoneDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

export function getCountryName(country) {
  const code = normalizeCountry(country);
  return REGION_NAMES?.of(code) || code;
}

export function getCountryFlagEmoji(country) {
  const code = normalizeCountry(country);
  return code
    .split("")
    .map((char) => String.fromCodePoint(char.charCodeAt(0) + 127397))
    .join("");
}

export function getCountryFlagSrc(country) {
  const code = normalizeCountry(country).toLowerCase();
  return `https://flagcdn.com/w40/${code}.png`;
}

export function detectPhoneCountry() {
  if (typeof navigator === "undefined") return DEFAULT_COUNTRY;
  const locales = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const locale of locales) {
    const match = String(locale || "").match(/[-_]([A-Za-z]{2})$/);
    if (!match?.[1]) continue;
    const country = normalizeCountry(match[1]);
    if (country) return country;
  }
  return DEFAULT_COUNTRY;
}

function getPhonePlaceholder(country) {
  const code = normalizeCountry(country);
  const example = getExampleNumber(code, examples);
  return example?.formatNational() || `+${getCountryCallingCode(code)}`;
}

function derivePhoneState(country, input, touched = false) {
  const nextCountry = normalizeCountry(country);
  const digits = sanitizePhoneDigits(input);
  const formatter = new AsYouType(nextCountry);
  const nationalNumber = digits ? formatter.input(digits) : "";
  const parsed = digits ? parsePhoneNumberFromString(digits, nextCountry) : null;
  const repeatedDigits = digits.length >= 6 && /^([0-9])\1+$/.test(digits);
  const zeroOnly = /^0+$/.test(digits);
  const isValid = Boolean(parsed?.isValid()) && !repeatedDigits && !zeroOnly;

  return {
    country: nextCountry,
    nationalNumber,
    e164: isValid ? parsed.number : "",
    formattedDisplay: isValid ? parsed.formatInternational() : nationalNumber,
    placeholder: getPhonePlaceholder(nextCountry),
    hasValue: digits.length > 0,
    isValid,
    touched,
    error:
      digits.length > 0 && !isValid
        ? `Please enter a valid phone number for ${getCountryName(nextCountry)}`
        : "",
  };
}

export function buildPhoneFieldValue(phone = "", country = detectPhoneCountry()) {
  const fallbackCountry = normalizeCountry(country);
  const raw = String(phone || "").trim();
  if (!raw) return derivePhoneState(fallbackCountry, "");
  const parsed = parsePhoneNumberFromString(raw, fallbackCountry);
  if (parsed) {
    return derivePhoneState(parsed.country || fallbackCountry, parsed.nationalNumber);
  }
  return derivePhoneState(fallbackCountry, raw);
}

export function updatePhoneFieldCountry(state, country) {
  return derivePhoneState(country, state?.nationalNumber || "", Boolean(state?.touched));
}

export function updatePhoneFieldNumber(state, input) {
  return derivePhoneState(state?.country || detectPhoneCountry(), input, Boolean(state?.touched));
}

export function touchPhoneField(state) {
  return { ...(state || buildPhoneFieldValue()), touched: true };
}

export function getPhoneFieldError(state) {
  if (!state?.hasValue) return "";
  return state.isValid ? "" : state.error;
}

export function formatPhoneForDisplay(phone) {
  const raw = String(phone || "").trim();
  if (!raw) return "";
  if (/[A-Za-z]/.test(raw)) return raw;
  const parsed = parsePhoneNumberFromString(raw, DEFAULT_COUNTRY);
  return parsed ? parsed.formatInternational() : raw;
}

export function isNormalizedPhone(phone) {
  return /^\+[1-9]\d{5,14}$/.test(String(phone || "").trim());
}

export function getPhoneHref(phone) {
  return isNormalizedPhone(phone) ? `tel:${String(phone).trim()}` : "";
}

export function getWhatsAppHref(phone) {
  if (!isNormalizedPhone(phone)) return "";
  return `https://wa.me/${String(phone).replace(/\D/g, "")}`;
}

export function searchPhoneCountryOptions(query = "") {
  const needle = String(query || "").trim().toLowerCase();
  if (!needle) return PHONE_COUNTRY_OPTIONS;
  return PHONE_COUNTRY_OPTIONS.filter((country) => country.searchText.includes(needle));
}

export const PHONE_COUNTRY_OPTIONS = getCountries()
  .map((country) => ({
    value: country,
    name: getCountryName(country),
    callingCode: `+${getCountryCallingCode(country)}`,
    flag: getCountryFlagEmoji(country),
    label: `${getCountryName(country)} (+${getCountryCallingCode(country)})`,
    searchText: `${getCountryName(country)} ${country} +${getCountryCallingCode(country)} ${getCountryCallingCode(country)}`.toLowerCase(),
  }))
  .sort((a, b) => {
    if (a.value === DEFAULT_COUNTRY) return -1;
    if (b.value === DEFAULT_COUNTRY) return 1;
    return a.label.localeCompare(b.label);
  });
